import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { AnyNetworkElement, NetworkElementType, Coordinates } from '../types/network';
import { useZoneStore } from './zoneStore';

interface NetworkUIState {
  selectedElement: AnyNetworkElement | null;
  /**
   * Elements within pixel tolerance of the map click that selected
   * selectedElement (cables in the same trench, stacked markers) — the
   * details panel offers them as alternatives. Only the map-click path sets
   * this; every other selection path clears it (a stale "at this spot" list
   * next to a search-selected element would lie).
   */
  coLocatedElementIds: string[];
  selectedElementType: NetworkElementType | null;
  isDragging: boolean;
  lastAction: {
    type: 'add' | 'remove' | 'connect' | 'disconnect' | 'power' | 'strang';
    elementId: string;
  } | null;
  visibleElementTypes: Set<NetworkElementType>;
  mapCenter: Coordinates | null;
  mapZoom: number | null;
  searchResults: AnyNetworkElement[];
  highlightedElementId: string | null;
  viewMode: 'map' | 'grid' | 'settings' | 'overload' | 'documentation';
  isControlSidebarCollapsed: boolean;
  isRightSidebarCollapsed: boolean;
}

interface NetworkUIActions {
  setSelectedElement: (element: AnyNetworkElement | null) => void;
  setSelectedElementFromMap: (element: AnyNetworkElement, coLocatedElementIds: string[]) => void;
  setSelectedElementType: (type: NetworkElementType | null) => void;
  setIsDragging: (isDragging: boolean) => void;
  setLastAction: (type: 'add' | 'remove' | 'connect' | 'disconnect' | 'power' | 'strang', elementId: string) => void;
  toggleElementTypeVisibility: (type: NetworkElementType) => void;
  setAllElementTypesVisible: (visible: boolean) => void;
  setMapView: (center: Coordinates, zoom: number) => void;
  setSearchResults: (results: AnyNetworkElement[]) => void;
  setHighlightedElement: (elementId: string | null) => void;
  setViewMode: (mode: 'map' | 'grid' | 'settings' | 'overload' | 'documentation') => void;
  toggleControlSidebarCollapsed: () => void;
  toggleRightSidebarCollapsed: () => void;
  setRightSidebarCollapsed: (collapsed: boolean) => void;
}

type NetworkUIStore = NetworkUIState & NetworkUIActions;

export const useNetworkUIStore = create<NetworkUIStore>()(
  devtools((set, get) => {
    // Expose state to window object
    (window as any).getSelectedElement = () => get().selectedElement;

    return {
      // State
      selectedElement: null,
      coLocatedElementIds: [],
      selectedElementType: null,
      isDragging: false,
      lastAction: null,
      visibleElementTypes: new Set(['trafostation', 'abgangsleitung', 'kabelverteilschrank', 'hausanschluss', 'muffe']),
      mapCenter: null,
      mapZoom: null,
      searchResults: [],
      highlightedElementId: null,
      viewMode: 'map' as const,
      isControlSidebarCollapsed: false,
      isRightSidebarCollapsed: false,

      // Actions
      setIsDragging: (isDragging: boolean) => {
        set({ isDragging }, false, 'setIsDragging');
      },

      setSelectedElement: (element: AnyNetworkElement | null) => {
        set({ selectedElement: element, coLocatedElementIds: [] }, false, 'setSelectedElement');
      },

      setSelectedElementFromMap: (element: AnyNetworkElement, coLocatedElementIds: string[]) => {
        set({ selectedElement: element, coLocatedElementIds }, false, 'setSelectedElementFromMap');
      },

      setSelectedElementType: (type: NetworkElementType | null) => {
        set({ selectedElementType: type }, false, 'setSelectedElementType');
      },

      setLastAction: (type: 'add' | 'remove' | 'connect' | 'disconnect' | 'power' | 'strang', elementId: string) => {
        set({ lastAction: { type, elementId } }, false, 'setLastAction');
      },

      toggleElementTypeVisibility: (type: NetworkElementType) => {
        set(state => {
          const newVisibleTypes = new Set(state.visibleElementTypes);
          if (newVisibleTypes.has(type)) {
            newVisibleTypes.delete(type);
          } else {
            newVisibleTypes.add(type);
          }
          return { visibleElementTypes: newVisibleTypes };
        }, false, 'toggleElementTypeVisibility');
      },

      setAllElementTypesVisible: (visible: boolean) => {
        set(() => ({
          visibleElementTypes: visible
            ? new Set(['trafostation', 'abgangsleitung', 'kabelverteilschrank', 'hausanschluss', 'muffe'])
            : new Set()
        }), false, 'setAllElementTypesVisible');
      },

      setMapView: (center: Coordinates, zoom: number) => {
        set({ mapCenter: center, mapZoom: zoom }, false, 'setMapView');
      },

      setSearchResults: (results: AnyNetworkElement[]) => {
        set({ searchResults: results }, false, 'setSearchResults');
      },

      setHighlightedElement: (elementId: string | null) => {
        set({ highlightedElementId: elementId }, false, 'setHighlightedElement');
      },

      setViewMode: (mode: 'map' | 'grid' | 'settings' | 'overload' | 'documentation') => {
        set({ viewMode: mode }, false, 'setViewMode');
      },

      toggleControlSidebarCollapsed: () => {
        set(state => ({ isControlSidebarCollapsed: !state.isControlSidebarCollapsed }), false, 'toggleControlSidebarCollapsed');
      },

      toggleRightSidebarCollapsed: () => {
        set(state => ({ isRightSidebarCollapsed: !state.isRightSidebarCollapsed }), false, 'toggleRightSidebarCollapsed');
      },

      setRightSidebarCollapsed: (collapsed: boolean) => {
        set({ isRightSidebarCollapsed: collapsed }, false, 'setRightSidebarCollapsed');
      }
    };
  })
);

// Clear zone-scoped view state whenever the active zone changes (covers all zone switch paths).
// mapCenter/mapZoom hold the last "jump to element" target (search, Netzzustand widget,
// Trennstellen, element tab); MapViewHandler skips its zone auto-fit while they are set,
// so a stale target from the previous zone would keep the map focused on that zone.
useZoneStore.subscribe((state, prevState) => {
  if (state.activeZoneId !== prevState.activeZoneId) {
    useNetworkUIStore.getState().setSelectedElement(null);
    useNetworkUIStore.setState({ mapCenter: null, mapZoom: null, highlightedElementId: null }, false, 'zoneChanged');
  }
});
